import {call, put, race, select, take, takeEvery} from "redux-saga/effects";
import {
    addLive4tvApiTargetRequest,
    checkpointRequired,
    checkpointSuccess, createAndStartLive4tvApiLiveStream,
    loginToLive4tvApiSuccess, sendACommentToLive4tvApi, sendACommentToLive4tvApiFail, stopLive4tvApiLiveStream,
    toggleComments, listTargets,
    types,
    updateLoginUrl,
} from "./live4tvApiActions";
import {StreamTargetIntegration} from "../StreamTargetIntegration/streamTargetIntegrationDTO";
import {StreamTargetDialog} from "../StreamTargetDialog/streamTargetDialogDTO";
import {closeStreamTargetDialog, openStreamTargetDialog} from "../StreamTargetDialog/streamTargetDialogActions";
import {
    LiveStreamRequest,
    StreamTargetList,
    StreamTargetKind,
    StreamTargetStatus,
    StreamTargetType
} from "../StreamTarget/streamTargetDTO";
import {
    Live4tvLiveStreamRequest,
    isInstanceOfLive4tvApiStreamTarget,
    Live4tvApiAuthRequest,
    Live4tvApiStreamTarget, Live4tvListStreamTargetsRequest, Live4tvRemoveStreamTargetRequest,
} from "./live4tvApiDTO";
import {Comment, CommentStatus} from "../Comments/CommentsDTO";
import {
    CreateAndStartLiveStreamRequest,
    CreateAndStartLiveStreamResponse,
    FetchLiveStreamCommentsResponse, Live4tvListAccountsResponse,
    live4tvLive4tvApiService,
    MuteCommentsRequest,
    ScheduledLiveListResponse,
    SendACommentToLiveStreamRequest,
    SendACommentToLiveStreamResponse,
    StartListeningToLiveStreamCommentsRequest,
    StartListeningToLiveStreamCommentsResponse,
    StopListeningToLiveStreamCommentsRequest,
    StopListeningToLiveStreamCommentsResponse,
    StopLiveStreamRequest,
    StopLiveStreamResponse
} from "./live4tvLive4tvApiService";
import {v4 as uuidv4} from 'uuid';
import {
    addStreamTarget,
    createAndStartLiveStreamFail,
    createAndStartLiveStreamSuccess, removeStreamTarget,
    sendACommentFail,
    sendACommentSuccess,
    setStreamTargetStatus,
    startListeningToCommentsFail,
    startListeningToCommentsSuccess,
    stopListeningToCommentsFail, stopListeningToCommentsRequest,
    stopListeningToCommentsSuccess,
    stopLiveStreamFail,
    stopLiveStreamSuccess
} from "../StreamTarget/streamTargetActions";
import {addComment} from "../Comments/commentsActions";
import delay from "../../services/helpers/delay";
import {ChannelSettingsType} from "../Channel/ChannelDTO";
import {isInstanceOfInstagramChannelSettings} from "../InstagramSettings/InstagramSettingsDTO";

function* live4tvApiSaga() {
    yield takeEvery(types.ADD_LIVE4TV_TARGET_REQUEST, addLive4tvTargetRequested)
    yield takeEvery(types.ADD_LIVE4TV_TARGET_CANCELED, addLive4tvTargetCanceled)

    yield takeEvery(types.LIVE4TV_LOGIN_REQUEST, live4tvLoginRequested);

    yield takeEvery(types.LIVE4TV_CREATE_AND_START_LIVE_STREAM_REQUEST, createAndStartLive4tvApiLiveStreamRequested);

    yield takeEvery(types.LIVE4TV_STOP_LIVE_STREAM_REQUEST, stopLive4tvApiLiveStreamRequested);

    yield takeEvery(types.LIVE4TV_START_LISTENING_TO_COMMENTS_REQUEST, startListeningToCommentsRequested);

    yield takeEvery(types.LIVE4TV_STOP_LISTENING_TO_COMMENTS_REQUEST, stopListeningToCommentsRequested);

    yield takeEvery(types.LIVE4TV_SEND_A_COMMENT_REQUEST, sendACommentRequested);

    yield takeEvery(types.LIVE4TV_SUBSCRIBE_TO_COMMENTS_REQUEST, subscribeToCommentsRequested);

    yield takeEvery(types.LIVE4TV_TOGGLE_COMMENTS_REQUEST, toggleCommentsRequested);

    yield takeEvery(types.LIVE4TV_CHECKPOINT_REQUEST, checkpointRequiredRequested);

    yield takeEvery(types.LIVE4TV_LIST_TARGETS_REQUEST, listTargetsRequested);

    yield takeEvery(types.LIVE4TV_DELETE_TARGET_REQUEST, deleteTargetRequested);
}

function* addLive4tvTargetRequested(action: { type: string; streamTargetIntegration: StreamTargetIntegration }) {
    yield put(openStreamTargetDialog(getEmptyRequestDialog(action.streamTargetIntegration)));
}

function getEmptyRequestDialog(streamTargetIntegration: StreamTargetIntegration) {
    const requestDialog: StreamTargetDialog = {
        isOpen: true,
        type: StreamTargetType.LIVE4TVAPI,
        props: {
            streamTargetIntegration,
        },
    }

    return requestDialog;
}

function* addLive4tvTargetCanceled() {
    yield put(closeStreamTargetDialog());
}

function* live4tvLoginRequested(action: {type: string, payload: Live4tvApiAuthRequest}) {
    console.debug('[SAGA] Live4tvApi login', action);
    try {
        const uuid = uuidv4();
        const authorizationUrl: string = yield call(generateLoginUrl, action.payload, uuid);
        yield put(updateLoginUrl(authorizationUrl));

        const streamTarget: Live4tvApiStreamTarget = yield call(login, action.payload, authorizationUrl, uuid);
        yield put(loginToLive4tvApiSuccess(streamTarget));
        yield put(addStreamTarget(streamTarget));
        yield put(closeStreamTargetDialog());
    } catch (e) {

    }
}

async function generateLoginUrl(request: Live4tvApiAuthRequest, uuid: string): Promise<string> {
    return await live4tvLive4tvApiService.getUrlAuthorizationUrl(
        request.profile.token,
        request.network,
        uuid,
        request.profile.code,
        request.instagramDriver,
    );
}

async function login(request: Live4tvApiAuthRequest, authorizationUrl: string, uuid: string): Promise<Live4tvApiStreamTarget> {
    window.open(authorizationUrl, "_blank", "noreferrer");

    const maxAttempts = 60;
    const intervalMilliSeconds = 5000;

    for (let i = 0; i <= maxAttempts; i++) {
        console.debug({i}, {intervalMilliSeconds});

        try {
            let response = await live4tvLive4tvApiService.getSocialNetworkAccount(
                request.profile.token,
                request.profile.code,
                uuid,
            );

            return <Live4tvApiStreamTarget>{
                key: `${response.type}_${response.name}`,
                uuid: response.uuid,
                type: response.type,
                targetKind: StreamTargetKind.SOCIAL_ACCOUNT,
                title: response.name,
                status: StreamTargetStatus.IDLE,
                commentStatus: CommentStatus.NOT_STARTED,
                isLive4tvApi: true,
            };
        } catch (e) {
            console.warn('Error getting access token', e);
        }

        await delay.sleep(intervalMilliSeconds);
    }

    throw new Error('Max attempts reached, live4TvApi getAccessToken');
}

function* checkpointRequiredRequested(action: {type: string, payload: Live4tvApiAuthRequest}) {
    console.debug('[SAGA] Checkpoint required', action);
    yield put(addLive4tvApiTargetRequest({
        code: action.payload.network,
        is_live4tv_api_integration: true,
    }));
    yield take(types.LIVE4TV_LOGIN_SUCCESS);

    yield put(checkpointSuccess(action.payload))
}

function refreshTargets(requestedTargets: Array<Live4tvApiStreamTarget>, refreshedStreamTargets: StreamTargetList): Array<Live4tvApiStreamTarget> {
    for (let i = 0; i < requestedTargets.length; i++) {
        const target = Object.values(refreshedStreamTargets).find((t: any) => {
            if (!isInstanceOfLive4tvApiStreamTarget(t)) {
                return false;
            }
            return t.uuid === requestedTargets[i].uuid && t.targetKind === requestedTargets[i].targetKind;
        });
        if (isInstanceOfLive4tvApiStreamTarget(target)) {
            requestedTargets[i] = target;
        }
    }

    return requestedTargets;
}

function toTargetRef(streamTarget: Live4tvApiStreamTarget) {
    return {
        kind: streamTarget.targetKind || StreamTargetKind.SOCIAL_ACCOUNT,
        uuid: streamTarget.uuid,
    };
}

function* createAndStartLive4tvApiLiveStreamRequested(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    console.debug('[SAGA] Create and start live4tvApi live stream', action);
    const streamTargets: StreamTargetList = yield select((state) => state.streamTargets);
    action.payload.streamTargets = yield call(refreshTargets, action.payload.streamTargets, streamTargets);

    try {
        for (let streamTarget of action.payload.streamTargets) {
            streamTarget.status = StreamTargetStatus.STARTING;
            yield put(setStreamTargetStatus(streamTarget));
        }

        const request: CreateAndStartLiveStreamRequest = {
            title: action.payload.liveEvent.title,
            description: action.payload.liveEvent.details,
            targets: action.payload.streamTargets.map((streamTarget: Live4tvApiStreamTarget) => toTargetRef(streamTarget)),
        }
        console.debug('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.request', request);

        const response: CreateAndStartLiveStreamResponse = yield call(
            live4tvLive4tvApiService.createAndStartLiveStream,
            action.payload.profile.token,
            action.payload.liveStreamUuid,
            request,
        );
        console.debug('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.response', response);

        for (let account of response.accounts) {
            const streamTarget = action.payload.streamTargets.find((streamTarget: Live4tvApiStreamTarget) => { return streamTarget.uuid === account.uuid });
            if (!isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                continue;
            }
            if (account.code === 200) {
                streamTarget.activationRequired = account.activationRequired === true;
                if (account.destinations && account.destinations.length > 0) {
                    streamTarget.transmissions = account.destinations.map((destination) => ({
                        url: destination.streamUrl,
                        key: destination.streamKey,
                        username: destination.username,
                        password: destination.password,
                    }));
                    streamTarget.transmission = streamTarget.transmissions[0];
                } else {
                    streamTarget.transmission = {
                        url: account.streamUrl,
                        key: account.streamKey,
                    }
                    if (account.username) {
                        streamTarget.transmission.username = account.username;
                    }
                    if (account.password) {
                        streamTarget.transmission.password = account.password;
                    }
                }
                yield put(createAndStartLiveStreamSuccess({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            } else if (account.code === 302) {
                console.warn('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.checkpoint', {...account});
                yield put(checkpointRequired(
                    {
                        network: streamTarget.type,
                        profile: action.payload.profile,
                        streamTarget,
                    },
                    'You must login again to your account to continue streaming',
                ));
                yield take(types.LIVE4TV_CHECKPOINT_SUCCESS);
                yield put(createAndStartLive4tvApiLiveStream({
                    profile: action.payload.profile,
                    streamTargets: [streamTarget],
                    liveEvent: action.payload.liveEvent,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    settings: action.payload.settings,
                }));

            } else {
                console.error('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.error', account.message);

                const socialNetworkName = streamTarget.title;
                // Map common HTTP status codes to user-friendly messages
                let errorMessage = account.message || 'Unknown error';
                if (account.message === 'failed' || !account.message) {
                    // Map status codes to more descriptive messages with social network context
                    switch (account.code) {
                        case 401:
                            errorMessage = `${socialNetworkName} authentication failed. Please reconnect your ${socialNetworkName} account.`;
                            break;
                        case 403:
                            errorMessage = `${socialNetworkName} access denied. Please check your ${socialNetworkName} account permissions.`;
                            break;
                        case 404:
                            errorMessage = `${socialNetworkName} account not found. Please reconnect your ${socialNetworkName} account.`;
                            break;
                        case 500:
                            errorMessage = `${socialNetworkName} server error. Please try again later.`;
                            break;
                        default:
                            errorMessage = account.message || `${socialNetworkName} failed to start stream (Error ${account.code})`;
                    }
                } else {
                    // If there's a custom message, prefix it with the social network name for clarity
                    errorMessage = `${socialNetworkName}: ${account.message}`;
                }
                streamTarget.errorMessage = errorMessage;
                yield put(createAndStartLiveStreamFail({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget: streamTarget,
                    settings: action.payload.settings,
                }));
            }
        }

        const successTargets = action.payload.streamTargets.filter((streamTarget: Live4tvApiStreamTarget) => { return response.accounts.find((account) => { return account.uuid === streamTarget.uuid && account.code === 200 }) });
        if (successTargets.length > 0) {
            yield put(toggleComments({
                profile: action.payload.profile,
                liveStreamUuid: action.payload.liveStreamUuid,
                liveEvent: action.payload.liveEvent,
                streamTargets: successTargets,
                settings: action.payload.settings,
            }));
        }
    } catch (e: any) {
        console.error('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.error', e);

        // Extract error data from response (for 402 Payment Required or other errors)
        // http-instance throws responseError.response.data for non-401/403 errors
        // Backend returns: {error: "message", reason: "...", additional_data: {...}}
        let errorReason: string | undefined;
        let errorAdditionalData: any | undefined;
        let errorMessage: string | undefined;

        if (e?.reason) {
            // When http-instance throws responseError.response.data, e is the response data object
            errorReason = e.reason;
            errorAdditionalData = e.additional_data || {};
            errorMessage = e.error; // Keep for fallback
        } else if (e?.response?.data?.reason) {
            // Fallback: if error structure is different (AxiosError format)
            errorReason = e.response.data.reason;
            errorAdditionalData = e.response.data.additional_data || {};
            errorMessage = e.response.data.error;
        } else if (e?.error) {
            // Fallback: just use error message if no reason
            errorMessage = typeof e.error === 'string' ? e.error : String(e.error);
        } else if (typeof e === 'string') {
            errorMessage = e;
        } else if (e?.message) {
            errorMessage = e.message;
        }

        for (let streamTarget of action.payload.streamTargets) {
            // Store error data in stream target for display with translation
            if (errorReason) {
                streamTarget.errorReason = errorReason;
                streamTarget.errorAdditionalData = errorAdditionalData;
            } else if (errorMessage) {
                // Fallback to plain error message if no reason code
                streamTarget.errorMessage = errorMessage;
            }
            yield put(createAndStartLiveStreamFail({
                token: action.payload.profile.token,
                liveStreamUuid: action.payload.liveStreamUuid,
                liveEvent: action.payload.liveEvent,
                streamTarget: streamTarget,
                settings: action.payload.settings,
            }));
        }
    }
}

function* stopLive4tvApiLiveStreamRequested(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    console.debug('[SAGA] Stop live4tvApi live stream', action);
    const streamTargets: StreamTargetList = yield select((state) => state.streamTargets);
    action.payload.streamTargets = yield call(refreshTargets, action.payload.streamTargets, streamTargets);

    try {
        for (let streamTarget of action.payload.streamTargets) {
            streamTarget.status = StreamTargetStatus.STOPPING;
            yield put(setStreamTargetStatus(streamTarget));
        }

        let saveLive = true;
        const settings = action.payload.settings[ChannelSettingsType.INSTAGRAM];
        if (isInstanceOfInstagramChannelSettings(settings)) {
            saveLive = settings.saveToIGTV;
        }

        const request: StopLiveStreamRequest = {
            targets: action.payload.streamTargets.map((streamTarget: Live4tvApiStreamTarget) => toTargetRef(streamTarget)),
            saveLive
        };

        console.debug('[SAGA] live4tvApi', 'stopLive4tvApiLiveStreamRequested.request', request);
        const response: StopLiveStreamResponse = yield call(
            live4tvLive4tvApiService.stopLiveStream,
            action.payload.profile.token,
            action.payload.liveStreamUuid,
            request,
        );

        for (let account of response.accounts) {
            const streamTarget = action.payload.streamTargets.find((streamTarget: Live4tvApiStreamTarget) => {
                return streamTarget.uuid === account.uuid
            });
            if (!isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                continue;
            }

            if (account.code === 200) {
                const liveStreamRequest: LiveStreamRequest = {
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                };
                yield put(stopLiveStreamSuccess(liveStreamRequest));

            } else if (account.code === 302) {
                console.warn('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.checkpoint', {...account});
                yield put(checkpointRequired(
                    {
                        network: streamTarget.type,
                        profile: action.payload.profile,
                        streamTarget: streamTarget,
                    },
                    'You must login again to your account to continue streaming',
                ));
                yield take(types.LIVE4TV_CHECKPOINT_SUCCESS);
                yield put(stopLive4tvApiLiveStream({
                    profile: action.payload.profile,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    settings: action.payload.settings,
                    streamTargets: [streamTarget],
                }));

            } else {
                console.error('[SAGA] live4tvApi', 'stopLive4tvApiLiveStreamRequested.error', account.message);
                yield put(stopLiveStreamFail({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            }
        }

        const stoppedScheduledLive = action.payload.streamTargets.some((streamTarget) => streamTarget.targetKind === StreamTargetKind.SCHEDULED_LIVE);
        if (stoppedScheduledLive) {
            yield put(listTargets({ profile: action.payload.profile }));
        }

    } catch (e) {
        console.error('[SAGA] live4tvApi', 'stopLive4tvApiLiveStreamRequested.error', e);
        for (let streamTarget of action.payload.streamTargets) {
            const liveStreamRequest: LiveStreamRequest = {
                token: action.payload.profile.token,
                liveStreamUuid: action.payload.liveStreamUuid,
                liveEvent: action.payload.liveEvent,
                streamTarget,
                settings: action.payload.settings,
            };
            yield put(stopLiveStreamFail(liveStreamRequest));
            // when the stop live fail, we must stop to listening comments, due the live is considered finished, and at moment there no way to point recovered to replay the action to stop trying to stop with success the live.
            yield put(stopListeningToCommentsRequest(liveStreamRequest));
        }
    }
}

function* subscribeToCommentsRequested(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    console.debug('[SAGA] subscribeToCommentsRequested', action);
    yield race({
        worker: call(fetchCommentsWorker, action),
        cancel: take(types.LIVE4TV_STOP_SUBSCRIPTION_TO_COMMENTS_REQUEST),
    });
}

function* fetchCommentsWorker(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    console.debug('[SAGA] Fetch comments', action);
    let lastCommentTimestamp = 0;

    while (true) {
        console.debug('[SAGA] Fetching comments', lastCommentTimestamp);
        try {
            const commentsResponse: FetchLiveStreamCommentsResponse = yield call(
                live4tvLive4tvApiService.fetchLiveStreamComments,
                action.payload.profile.token,
                action.payload.liveStreamUuid,
                lastCommentTimestamp,
            );
            console.debug('[SAGA] live4tvApi', 'fetchCommentsWorker.response', commentsResponse);

            if (commentsResponse.count > 0) {
                for (let i = 0; i < commentsResponse.count; i++) {
                    let comment: Comment = {
                        streamTargetType: commentsResponse.comments[i].socialNetwork,
                        key: commentsResponse.comments[i].uuid,
                        username: commentsResponse.comments[i].user.username,
                        photo: commentsResponse.comments[i].user.photo,
                        message: commentsResponse.comments[i].message,
                        time: new Date(commentsResponse.comments[i].sentAt * 1000),
                    };
                    lastCommentTimestamp = commentsResponse.lastCommentTimestamp;
                    yield put(addComment(action.payload.liveEvent, comment));
                }
            }
        } catch (e) {
            console.error('[SAGA] Fetch comments error', e);
        }

        yield delay.sleep(2000);
    }
}

function* startListeningToCommentsRequested(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    console.debug('[SAGA] Start listening to comments', action);
    try {
        const request: StartListeningToLiveStreamCommentsRequest = {
            targets: action.payload.streamTargets.map((streamTarget: Live4tvApiStreamTarget) => toTargetRef(streamTarget)),
        }
        console.debug('[SAGA] live4tvApi', 'startListeningToCommentsRequested.request', request);

        const response: StartListeningToLiveStreamCommentsResponse = yield call(
            live4tvLive4tvApiService.startListeningToComments,
            action.payload.profile.token,
            action.payload.liveStreamUuid,
            request,
        );
        console.debug('[SAGA] live4tvApi', 'startListeningToCommentsRequested.response', response);

        for (let account of response.accounts) {
            const streamTarget = action.payload.streamTargets.find((streamTarget: Live4tvApiStreamTarget) => { return streamTarget.uuid === account.uuid });
            console.debug('[SAGA] live4tvApi', 'startListeningToCommentsRequested.streamTarget', streamTarget);
            if (!isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                continue;
            }
            if (account.code === 200) {
                yield put(startListeningToCommentsSuccess({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            } else {
                console.error('[SAGA] live4tvApi', 'startListeningToCommentsRequested.error', account.message);
                yield put(startListeningToCommentsFail({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            }
        }
    } catch (e) {
        console.error('[SAGA] live4tvApi', 'startListeningToCommentsRequested.error', e);
        for (let streamTarget of action.payload.streamTargets) {
            yield put(startListeningToCommentsFail({
                token: action.payload.profile.token,
                liveStreamUuid: action.payload.liveStreamUuid,
                liveEvent: action.payload.liveEvent,
                streamTarget,
                settings: action.payload.settings,
            }));
        }
    }
}

function* stopListeningToCommentsRequested(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    console.debug('[SAGA] Stop listening to comments', action);
    try {
        const request: StopListeningToLiveStreamCommentsRequest = {
            targets: action.payload.streamTargets.map((streamTarget: Live4tvApiStreamTarget) => toTargetRef(streamTarget)),
        }
        console.debug('[SAGA] live4tvApi', 'stopListeningToCommentsRequested.request', request);

        const response: StopListeningToLiveStreamCommentsResponse = yield call(
            live4tvLive4tvApiService.stopListeningToComments,
            action.payload.profile.token,
            action.payload.liveStreamUuid,
            request,
        );
        console.debug('[SAGA] live4tvApi', 'stopListeningToCommentsRequested.response', response);

        for (let account of response.accounts) {
            const streamTarget = action.payload.streamTargets.find((streamTarget: Live4tvApiStreamTarget) => { return streamTarget.uuid === account.uuid });
            if (!isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                continue;
            }
            if (account.code === 200) {
                yield put(stopListeningToCommentsSuccess({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            } else {
                console.error('[SAGA] live4tvApi', 'stopListeningToCommentsRequested.error', account.message);
                yield put(stopListeningToCommentsFail({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            }
        }

    } catch (e) {
        console.error('[SAGA] live4tvApi', 'stopListeningToCommentsRequested.error', e);
        for (let streamTarget of action.payload.streamTargets) {
            yield put(stopListeningToCommentsFail({
                token: action.payload.profile.token,
                liveStreamUuid: action.payload.liveStreamUuid,
                liveEvent: action.payload.liveEvent,
                streamTarget,
                settings: action.payload.settings,
            }));
        }
    }
}

function* sendACommentRequested(action: {type: string, payload: Live4tvLiveStreamRequest, message: string}) {
    console.debug('[SAGA] Send a comment', action);
    const streamTargets: StreamTargetList = yield select((state) => state.streamTargets);
    action.payload.streamTargets = yield call(refreshTargets, action.payload.streamTargets, streamTargets);

    try {
        const request: SendACommentToLiveStreamRequest = {
            message: action.message,
            targets: action.payload.streamTargets.map((streamTarget: Live4tvApiStreamTarget) => toTargetRef(streamTarget)),
        }
        console.debug('[SAGA] live4tvApi', 'sendACommentRequested.request', request);

        const response: SendACommentToLiveStreamResponse = yield call(
            live4tvLive4tvApiService.sendACommentToLiveStream,
            action.payload.profile.token,
            action.payload.liveStreamUuid,
            request,
        );
        console.debug('[SAGA] live4tvApi', 'sendACommentRequested.response', response);

        for (let account of response.accounts) {
            const streamTarget = action.payload.streamTargets.find((streamTarget: Live4tvApiStreamTarget) => {
                return streamTarget.uuid === account.uuid
            });
            if (!isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                continue;
            }

            if (account.code === 200) {
                yield put(sendACommentSuccess({
                    token: action.payload.profile.token,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTarget,
                    settings: action.payload.settings,
                }));
            } else if (account.code === 302) {
                console.warn('[SAGA] live4tvApi', 'createAndStartLive4tvApiLiveStreamRequested.checkpoint', {...account});
                yield put(checkpointRequired(
                    {
                        network: streamTarget.type,
                        profile: action.payload.profile,
                        streamTarget: streamTarget,
                    },
                    'You must login again to your account to continue streaming',
                ));
                yield take(types.LIVE4TV_CHECKPOINT_SUCCESS);
                yield put(sendACommentToLive4tvApi({
                    profile: action.payload.profile,
                    liveStreamUuid: action.payload.liveStreamUuid,
                    liveEvent: action.payload.liveEvent,
                    streamTargets: [streamTarget],
                    settings: action.payload.settings,
                }, action.message));
            } else {
                yield put(sendACommentToLive4tvApiFail(action.payload, action.message));
            }
        }


    } catch (e) {
        console.error('[SAGA] live4tvApi', 'sendACommentRequested.error', e);

        yield put(sendACommentFail({
            token: action.payload.profile.token,
            liveStreamUuid: action.payload.liveStreamUuid,
            liveEvent: action.payload.liveEvent,
            streamTarget: action.payload.streamTargets[0],
            settings: action.payload.settings,
        }));
    }
}

function* toggleCommentsRequested(action: {type: string, payload: Live4tvLiveStreamRequest}) {
    const streamTargets: StreamTargetList = yield select((state) => state.streamTargets);
    action.payload.streamTargets = yield call(refreshTargets, action.payload.streamTargets, streamTargets);

    const request: MuteCommentsRequest = {
        targets: action.payload.streamTargets
            .filter((streamTarget: Live4tvApiStreamTarget) => { return streamTarget.status === StreamTargetStatus.LIVE })
            .map((streamTarget: Live4tvApiStreamTarget) => toTargetRef(streamTarget)),
    }

    if ((request.targets || []).length === 0) {
        console.warn('[SAGA] live4tvApi', 'toggleCommentsRequested', 'No accounts to mute');
    }

    try {
        const settings = action.payload.settings[ChannelSettingsType.INSTAGRAM];
        if (settings && isInstanceOfInstagramChannelSettings(settings)) {
            if (settings.allowComments) {
                yield call(live4tvLive4tvApiService.unmute, action.payload.profile.token, action.payload.liveStreamUuid, request);
            } else {
                yield call(live4tvLive4tvApiService.mute, action.payload.profile.token, action.payload.liveStreamUuid, request);
            }
        }
    } catch (e) {
        console.error('[SAGA] live4tvApi', 'toggleCommentsRequested.error', e);
    }
}

function* listTargetsRequested(action: { type: string, payload: Live4tvListStreamTargetsRequest }) {
    if (!action.payload?.profile?.token || !action.payload?.profile?.code) {
        console.warn('[SAGA] live4tvApi', 'listTargetsRequested.skipped', 'Missing profile token or code');
        return;
    }

    const streamTargets: StreamTargetList = yield select((state) => state.streamTargets);
    let accountsResponse: Live4tvListAccountsResponse = [];
    let scheduledLivesResponse: ScheduledLiveListResponse = { data: [] };

    try {
        accountsResponse = yield call(
            live4tvLive4tvApiService.listAccounts,
            action.payload.profile.token,
            action.payload.profile.code,
        );
        console.debug('[SAGA] live4tvApi', 'listTargetsRequested.response', accountsResponse);
    } catch (e) {
        console.error('[SAGA] live4tvApi', 'listTargetsRequested.accounts.error', e);
    }

    try {
        scheduledLivesResponse = yield call(
            live4tvLive4tvApiService.listScheduledLives,
            action.payload.profile.token,
            action.payload.profile.code,
        );
    } catch (e) {
        console.error('[SAGA] live4tvApi', 'listTargetsRequested.scheduledLives.error', e);
    }

    try {
        for (let account of accountsResponse) {
            const streamTarget = Object.values(streamTargets).find((t: any) => {
                return isInstanceOfLive4tvApiStreamTarget(t)
                    && t.targetKind === StreamTargetKind.SOCIAL_ACCOUNT
                    && (t.uuid === account.uuid || (t.type === account.type && t.title === account.name));
            });
            if (streamTarget !== undefined && !isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                console.warn('[SAGA] live4tvApi', 'listTargetsRequested', 'Invalid stream target', streamTarget);
                continue;
            }

            if (streamTarget && streamTarget.uuid !== account.uuid) {
                yield put(removeStreamTarget(streamTarget));
            }

            yield put(addStreamTarget(<Live4tvApiStreamTarget>{
                ...streamTarget,
                key: `${account.type}_${account.name}`,
                uuid: account.uuid,
                type: account.type,
                targetKind: StreamTargetKind.SOCIAL_ACCOUNT,
                title: account.name,
                status: streamTarget?.status || StreamTargetStatus.IDLE,
                commentStatus: streamTarget?.commentStatus || CommentStatus.NOT_STARTED,
                isLive4tvApi: true,
            }));
        }

        const activeScheduledLiveUuids = new Set<string>();
        for (const scheduledLive of scheduledLivesResponse.data.filter((item) => item.status !== 'completed')) {
            activeScheduledLiveUuids.add(scheduledLive.uuid);
            const existingTarget = Object.values(streamTargets).find((target: any) => {
                return isInstanceOfLive4tvApiStreamTarget(target)
                    && target.targetKind === StreamTargetKind.SCHEDULED_LIVE
                    && target.uuid === scheduledLive.uuid;
            });

            yield put(addStreamTarget(<Live4tvApiStreamTarget>{
                ...existingTarget,
                key: `scheduled_live_${scheduledLive.uuid}`,
                uuid: scheduledLive.uuid,
                type: StreamTargetType.SCHEDULED_LIVE,
                targetKind: StreamTargetKind.SCHEDULED_LIVE,
                title: scheduledLive.title,
                status: existingTarget?.status || StreamTargetStatus.IDLE,
                commentStatus: existingTarget?.commentStatus || CommentStatus.NOT_STARTED,
                isLive4tvApi: true,
                schedule: {
                    scheduledAt: scheduledLive.scheduledAt,
                    status: scheduledLive.status,
                    accounts: scheduledLive.accounts,
                },
            }));
        }

        for (const target of Object.values(streamTargets) as Array<any>) {
            if (
                isInstanceOfLive4tvApiStreamTarget(target)
                && target.targetKind === StreamTargetKind.SCHEDULED_LIVE
                && !activeScheduledLiveUuids.has(target.uuid)
            ) {
                const isRunning = [StreamTargetStatus.LIVE, StreamTargetStatus.STARTING, StreamTargetStatus.STOPPING].includes(target.status);
                if (!isRunning) {
                    yield put(removeStreamTarget(target));
                }
            }
        }
    } catch (e) {
        console.error('[SAGA] live4tvApi', 'listTargetsRequested.error', e);
    }
}

function* deleteTargetRequested(action: {type: string, payload: Live4tvRemoveStreamTargetRequest}) {
    try {
        yield call(
            live4tvLive4tvApiService.deleteAccount,
            action.payload.profile.token,
            action.payload.streamTarget.uuid
        );
    } catch (e) {
        console.error('[SAGA] live4tvApi', 'deleteTargetRequested.error', e);
    }
}

export default live4tvApiSaga;
